import { useMemo } from 'react';

import { RedeemRewardView } from '@fhs-legacy/frontend/src/components/cart-item/redeem-reward/redeem-reward.view';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { ICartEntry } from '@rbi-ctg/menu';

import { useAddIncentiveToCart } from '../../hooks/use-add-incentive-to-cart';
import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useRemoveOffer } from '../../hooks/use-remove-offer';
import { useRewardTierApplied } from '../../hooks/use-reward-tier-applied';
import { useRewardTiers } from '../../queries/loyalty.queries';

interface Props {
  item: ICartEntry;
}

export function RedeemRewardTierButton({ item }: Props) {
  const { loyaltyUser } = useLoyaltyLegacyStates();
  const { data } = useRewardTiers();
  const { addIncentiveToCart } = useAddIncentiveToCart();
  const { removeOffer } = useRemoveOffer();

  const { rewardTierApplied } = useRewardTierApplied();

  const eligibleRewardTier = useMemo(
    () => data?.find(tier => tier.eligibleItems.some(itemId => itemId === item._id)),
    [data, item._id]
  );

  const hasEnoughPoints =
    eligibleRewardTier && loyaltyUser?.points && eligibleRewardTier.points <= loyaltyUser.points;
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);

  const hasAppliedOffers = appliedOffers.length > 0;
  const incentiveSelections = useAppSelector(selectors.loyalty.selectSelectedOfferSelections);

  const isRewardApplied = useMemo(
    () =>
      incentiveSelections.some(
        selection =>
          selection.incentiveId === rewardTierApplied?.id && selection.lineId === item.cartId
      ),
    [incentiveSelections, rewardTierApplied, item.cartId]
  );

  const displayRewardTier = rewardTierApplied || eligibleRewardTier;

  const onPressRewardTier = () => () => {
    if (!displayRewardTier) {
      return;
    }
    if (isRewardApplied) {
      removeOffer({ showDialog: false });
      return;
    }
    addIncentiveToCart({
      offer: {
        incentiveType: 'REWARD',
        id: displayRewardTier.id,
        cmsId: displayRewardTier.id,
        name: displayRewardTier.name,
        description: '',
        requireGuideFlow: true,
        requireTierSelector: false,
        pricingStrategy: {
          pricingStrategy: 'DISCOUNT',
          type: 'percentage',
          value: 100,
        },
      },
      selections: [
        {
          incentiveId: displayRewardTier.id,
          key: 'appliedProduct',
          lineId: item.cartId,
          quantity: 1,
          rank: 0,
        },
      ],
    });
  };

  if (!displayRewardTier || (hasAppliedOffers && !isRewardApplied)) {
    return null;
  }

  return (
    <RedeemRewardView
      handleRewardClick={onPressRewardTier}
      useTitleLabel={displayRewardTier.points === 0}
      points={displayRewardTier.points}
      rewardTitle={displayRewardTier.name}
      isRewardApplied={isRewardApplied}
      displayApplyButton={!isRewardApplied}
      pointsApplied={isRewardApplied ? displayRewardTier.points : 0}
      incentiveNotInMenu={false}
      redeemRewardLoading={false}
      disableApplyButton={!hasEnoughPoints}
    />
  );
}
